import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import {
  useListProjects,
  useCreateProject,
  getListProjectsQueryKey,
  getListTasksQueryKey,
  getGetTodayQueryKey,
  type ProjectSummary,
} from "@workspace/api-client-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Folder,
  Plus,
  Loader2,
  ArrowRight,
  CalendarClock,
  Flag,
  Sparkles,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import SuggestedProjectsSection from "@/components/suggested-projects-section";
import {
  listProjectSuggestions,
  acceptProjectSuggestion,
  dismissProjectSuggestion,
  type ProjectSuggestion,
} from "@/lib/project-suggestions-api";

const PROJECT_SUGGESTIONS_QUERY_KEY = ["project-suggestions"] as const;

const STATUS_FILTER_VALUES = ["all", "active", "blocked", "paused", "done"] as const;
const SORT_OPTION_VALUES = ["recent", "deadline", "priority", "name"] as const;

const PRIORITY_CLASSES: Record<string, string> = {
  high: "text-destructive border-destructive/40",
  medium: "text-amber-600 border-amber-500/40",
  low: "text-muted-foreground border-border",
};

function CreateProjectDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<string>("medium");
  const [deadline, setDeadline] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createProject = useCreateProject();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    createProject.mutate(
      {
        data: {
          name,
          description: description || null,
          priority,
          deadline: deadline ? new Date(deadline).toISOString() : null,
        },
      },
      {
        onSuccess: () => {
          toast({ title: t("projects_page.create_toast") });
          setOpen(false);
          setName("");
          setDescription("");
          setPriority("medium");
          setDeadline("");
          queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
        },
        onError: () => toast({ title: t("common.create_error"), variant: "destructive" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full shrink-0">
          <Plus className="w-4 h-4 mr-2" /> {t("projects_page.new_project_btn")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t("projects_page.dialog_title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("projects_page.name_label")}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects_page.name_placeholder")}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">{t("projects_page.desc_label")}</Label>
            <Textarea
              id="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("projects_page.desc_placeholder")}
              className="resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="priority">{t("projects_page.priority_label")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">{t("projects_page.priority_high")}</SelectItem>
                  <SelectItem value="medium">{t("projects_page.priority_medium")}</SelectItem>
                  <SelectItem value="low">{t("projects_page.priority_low")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deadline">{t("projects_page.deadline_label")}</Label>
              <Input
                id="deadline"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={!name.trim() || createProject.isPending}
              className="w-full sm:w-auto"
            >
              {createProject.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("projects_page.create_btn")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function priorityRank(p: string | null | undefined): number {
  switch (p) {
    case "high":
      return 0;
    case "medium":
      return 1;
    case "low":
      return 2;
    default:
      return 3;
  }
}

function SuggestedProjectGridCard({
  suggestion,
  onAccepted,
  onDismissed,
}: {
  suggestion: ProjectSuggestion;
  onAccepted: (projectId: string) => void;
  onDismissed: () => void;
}) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);

  const acceptMutation = useMutation({
    mutationFn: () =>
      acceptProjectSuggestion(suggestion.id, {
        name: suggestion.name,
        description: suggestion.description,
      }),
    onSuccess: (r) => {
      toast({
        title: "Projet créé",
        description: `${r.attachedTaskCount} tâche${r.attachedTaskCount !== 1 ? "s" : ""} rattachée${r.attachedTaskCount !== 1 ? "s" : ""}.`,
      });
      onAccepted(r.project.id);
    },
    onError: (err: Error) =>
      toast({ title: "Création impossible", description: err.message, variant: "destructive" }),
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissProjectSuggestion(suggestion.id),
    onSuccess: () => {
      toast({ title: "Suggestion ignorée" });
      onDismissed();
    },
    onError: (err: Error) =>
      toast({ title: "Action impossible", description: err.message, variant: "destructive" }),
  });

  const isPending = acceptMutation.isPending || dismissMutation.isPending;
  const taskCount = suggestion.tasks.length;

  return (
    <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] flex flex-col">
      {/* Header — always visible, click to expand */}
      <CardContent
        className="p-5 flex flex-col gap-3 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <h3 className="font-semibold text-base text-foreground line-clamp-2 leading-tight">
              {suggestion.name}
            </h3>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge className="bg-primary/15 text-primary border-primary/25 text-[11px] font-medium">
              Suggéré
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mr-1"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              aria-label={expanded ? "Réduire" : "Développer"}
            >
              {expanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </Button>
          </div>
        </div>

        {/* Meta row — task count, always visible */}
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <Badge variant="secondary" className="font-mono text-xs">
            {taskCount === 1 ? "1 tâche" : `${taskCount} tâches`}
          </Badge>
          <Badge variant="outline" className="font-normal text-muted-foreground">
            À créer
          </Badge>
        </div>

        {/* Progress bar placeholder (0 tasks completed yet) */}
        {taskCount > 0 && (
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-[11px] text-muted-foreground">0 / {taskCount} terminées</span>
              <span className="text-[11px] font-semibold text-primary tabular-nums">0%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full w-0" />
            </div>
          </div>
        )}
      </CardContent>

      {/* Expandable detail section */}
      {expanded && (
        <div className="px-5 pb-5 space-y-3 border-t border-primary/10 pt-3">
          {suggestion.reason && (
            <p className="text-xs text-muted-foreground italic">{suggestion.reason}</p>
          )}

          {suggestion.tasks.length > 0 && (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {suggestion.tasks.map((task) => (
                <li key={task.id} className="flex items-start gap-2 text-xs text-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0 mt-1.5" />
                  <span className="line-clamp-1">{task.title}</span>
                </li>
              ))}
            </ul>
          )}

          <div
            className="flex items-center gap-2 pt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="sm"
              className="rounded-full flex-1"
              onClick={() => acceptMutation.mutate()}
              disabled={isPending}
            >
              {acceptMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              )}
              Créer le projet
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full text-muted-foreground hover:text-destructive"
              onClick={() => dismissMutation.mutate()}
              disabled={isPending}
            >
              {dismissMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <X className="w-3.5 h-3.5 mr-1" />
              )}
              Ignorer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ProjectsPage() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [sortBy, setSortBy] = useState<string>("recent");
  const qc = useQueryClient();

  const { data: projects, isLoading, error } = useListProjects(
    statusFilter === "all" ? {} : { status: statusFilter },
  );

  const { data: suggestionsData } = useQuery({
    queryKey: PROJECT_SUGGESTIONS_QUERY_KEY,
    queryFn: listProjectSuggestions,
    staleTime: 30_000,
  });
  const suggestions = suggestionsData?.suggestions ?? [];

  const sorted: ProjectSummary[] = useMemo(() => {
    const list = [...(projects ?? [])];
    switch (sortBy) {
      case "deadline":
        return list.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        });
      case "priority":
        return list.sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority));
      case "name":
        return list.sort((a, b) => a.name.localeCompare(b.name, "fr"));
      case "recent":
      default:
        return list.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }
  }, [projects, sortBy]);

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto pb-24 md:pb-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1">{t("projects_page.title")}</h1>
          <p className="text-muted-foreground">{t("projects_page.subtitle")}</p>
        </div>
        <CreateProjectDialog />
      </header>

      <SuggestedProjectsSection />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-full" aria-label={t("projects_page.filter_aria")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_VALUES.map((v) => (
              <SelectItem key={v} value={v}>
                {t(`projects_page.filter_${v}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[160px] rounded-full" aria-label={t("projects_page.sort_aria")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTION_VALUES.map((v) => (
              <SelectItem key={v} value={v}>
                {t(`projects_page.sort_${v}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="rounded-2xl">
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-10 w-full mt-4" />
              </CardContent>
            </Card>
          ))
        ) : error ? (
          <div className="col-span-2 text-center p-8 text-destructive border border-destructive/20 rounded-2xl bg-destructive/5">
            {t("projects_page.load_error")}
          </div>
        ) : sorted.length === 0 && suggestions.length === 0 ? (
          <div className="col-span-2 text-center p-12 border border-border/50 border-dashed rounded-3xl bg-secondary/20">
            <Folder className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">
              {statusFilter === "all" ? t("projects_page.empty_title") : t("projects_page.empty_filtered")}
            </h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-4">
              {t("projects_page.empty_desc")}
            </p>
            {statusFilter !== "all" && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setStatusFilter("all")}
              >
                Voir tous les projets
              </Button>
            )}
          </div>
        ) : (
          <>
            {suggestions.map((s) => (
              <SuggestedProjectGridCard
                key={s.id}
                suggestion={s}
                onAccepted={(projectId) => {
                  qc.invalidateQueries({ queryKey: PROJECT_SUGGESTIONS_QUERY_KEY });
                  qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
                  qc.invalidateQueries({ queryKey: getListTasksQueryKey(), exact: false });
                  qc.invalidateQueries({ queryKey: getGetTodayQueryKey() });
                  navigate(`/app/projects/${projectId}`);
                }}
                onDismissed={() => qc.invalidateQueries({ queryKey: PROJECT_SUGGESTIONS_QUERY_KEY })}
              />
            ))}
            {sorted.map((project) => (
            <Link key={project.id} href={`/app/projects/${project.id}`}>
              <Card className="rounded-2xl hover:border-primary/50 transition-colors cursor-pointer group h-full flex flex-col">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1">
                      {project.name}
                    </h3>
                    <Badge variant="secondary" className="font-mono text-xs shrink-0">
                      {t(project.openTaskCount === 1 ? "projects_page.task_count_one" : "projects_page.task_count_other", { count: project.openTaskCount })}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 text-xs flex-wrap mb-3">
                    <Badge variant="outline" className="font-normal">
                      {t(`status.${project.status}`, project.status)}
                    </Badge>
                    {project.priority && (
                      <Badge
                        variant="outline"
                        className={`font-normal ${
                          PRIORITY_CLASSES[project.priority] ?? ""
                        }`}
                      >
                        <Flag className="w-3 h-3 mr-1" />
                        {t(`priority.${project.priority}`, project.priority)}
                      </Badge>
                    )}
                    {project.deadline && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <CalendarClock className="w-3 h-3" />
                        {format(new Date(project.deadline), "d MMM yyyy", { locale: dateLocale })}
                      </span>
                    )}
                  </div>

                  {project.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
                      {project.description}
                    </p>
                  )}

                  {/* Progress bar */}
                  {(() => {
                    const total = project.openTaskCount + (project.doneTaskCount ?? 0);
                    const done = project.doneTaskCount ?? 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    return total > 0 ? (
                      <div className="mb-3">
                        <div className="flex justify-between items-baseline mb-1.5">
                          <span className="text-[11px] text-muted-foreground">
                            {t("projects_page.progress_label", { done, total })}
                          </span>
                          <span className="text-[11px] font-semibold text-primary tabular-nums">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    ) : null;
                  })()}

                  <div className="mt-auto pt-3 border-t border-border/50">
                    {project.nextAction ? (
                      <div>
                        <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1">
                          {t("projects_page.next_action")}
                        </div>
                        <div className="text-sm font-medium flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-accent shrink-0"></span>
                          <span className="line-clamp-1">{project.nextAction.title}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-orange-500 font-medium flex items-center gap-1.5">
                        <ArrowRight className="w-4 h-4" /> {t("projects_page.waiting_action")}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
          </>
        )}
      </div>
    </div>
  );
}
